/**
 * CodeMirror 6 extensions that give the markdown editor its *live inline formatting*.
 *
 * ── WHY THERE IS NO CONVERSION STEP ───────────────────────────────────────────────────────────
 * The document in the editor is, at every instant, the same plain markdown string that gets
 * PATCHed to the API. Nothing is parsed into HTML, nothing is serialised back. CodeMirror parses
 * the markdown only to decide *how to paint the characters that are already there* — a heading
 * line is drawn larger, `**bold**` is drawn bold, a fenced block is drawn monospace on a tinted
 * background. The syntax markers (`#`, `**`, `` ` ``) stay visible and stay editable; they are
 * just dimmed so the content reads ahead of them. That is the whole mechanism, and it is why this
 * approach has nothing to lose fidelity in: `view.state.doc.toString()` is byte-identical to what
 * the user typed.
 *
 * This is deliberately NOT rich-text/WYSIWYG editing. The user still authors markdown syntax.
 *
 * ── HOW THE STYLING IS WIRED ──────────────────────────────────────────────────────────────────
 * Three layers, in increasing scope:
 *
 *   1. `markdownSemanticClasses` — a `HighlightStyle` that assigns a readable class name per
 *      markdown tag and *no* style properties. `HighlightStyle.define` would happily take the
 *      styles too, but it then emits them under generated names (`ͼ7`), which are unreadable in
 *      devtools and unassertable in tests. Declaring the classes here and the rules in the theme
 *      keeps every visual value in one themeable block.
 *
 *   2. `markdownLiveTheme` — the actual rules, keyed on those class names.
 *
 *   3. `blockLineDecorations` — line classes for constructs whose *whole line* must change:
 *      fenced code, blockquotes and tables. A table only needs its pipes to line up, which a
 *      monospace line class achieves without any table rendering — replacing the source text with
 *      a rendered table widget is exactly the round-trip this design avoids.
 *
 * ── THE COMBINED-CLASS TRICK ──────────────────────────────────────────────────────────────────
 * `lang-markdown` tags lists as `"BulletList/... OrderedList/..."`, i.e. the `list` tag lands on
 * every *descendant* of a list, not on the bullet. Styling `t.list` directly would therefore tint
 * the entire list item. CodeMirror's highlighter unions the classes of all enclosing tagged nodes
 * onto one span, so a `ListMark` ends up with BOTH `cm-md-list` and `cm-md-syntax` — and
 * `.cm-md-list.cm-md-syntax` targets the bullet and nothing else. The same union is why inline
 * code inside a fenced block has to be reset: `CodeText` carries `t.monospace`, so the chip
 * styling is scoped away from code-block lines rather than applied blindly.
 *
 * All colours, radii and fonts come from the design tokens in `src/styles/globals.css` (`@theme`
 * emits them as real CSS custom properties), per frontend-standards.md §1 — no ad-hoc values.
 */

import { RangeSetBuilder } from '@codemirror/state';
import {
  Decoration,
  EditorView,
  ViewPlugin,
  type DecorationSet,
  type ViewUpdate,
} from '@codemirror/view';
import { HighlightStyle, syntaxHighlighting, syntaxTree } from '@codemirror/language';
import { tags as t } from '@lezer/highlight';

/** Monospace stack. Tailwind v4 ships `--font-mono`; the literals are a defensive fallback. */
const MONO = 'var(--font-mono, ui-monospace, SFMono-Regular, Menlo, Consolas, monospace)';

/**
 * Token → class name. Style properties deliberately live in `markdownLiveTheme`, not here.
 *
 * `t.processingInstruction` is the markdown syntax itself (`##`, `**`, `>`, `` ` ``, `-`). Dimming
 * it rather than hiding it is the point: the user is editing markdown, so the markers must stay
 * legible — they simply stop competing with the prose.
 */
export const markdownSemanticClasses = HighlightStyle.define([
  { tag: t.heading1, class: 'cm-md-h1' },
  { tag: t.heading2, class: 'cm-md-h2' },
  { tag: t.heading3, class: 'cm-md-h3' },
  { tag: t.heading4, class: 'cm-md-h4' },
  { tag: t.heading5, class: 'cm-md-h5' },
  { tag: t.heading6, class: 'cm-md-h6' },
  { tag: t.strong, class: 'cm-md-strong' },
  { tag: t.emphasis, class: 'cm-md-em' },
  { tag: t.strikethrough, class: 'cm-md-strike' },
  { tag: t.monospace, class: 'cm-md-code' },
  { tag: t.link, class: 'cm-md-link' },
  { tag: t.url, class: 'cm-md-url' },
  { tag: t.quote, class: 'cm-md-quote' },
  { tag: t.contentSeparator, class: 'cm-md-hr' },
  // Carries no rule of its own — it exists so `.cm-md-list.cm-md-syntax` can target bullets.
  { tag: t.list, class: 'cm-md-list' },
  { tag: t.processingInstruction, class: 'cm-md-syntax' },
]);

/**
 * Line classes for block constructs, keyed by lezer-markdown node name.
 * `CodeBlock` is the indented (four-space) form; `FencedCode` is the ``` form.
 */
const BLOCK_LINE_CLASSES: Record<string, string> = {
  FencedCode: 'cm-md-codeblock',
  CodeBlock: 'cm-md-codeblock',
  Blockquote: 'cm-md-blockquote',
  Table: 'cm-md-table',
};

const BLOCK_LINE_DECORATIONS: Record<string, Decoration> = Object.fromEntries(
  Object.entries(BLOCK_LINE_CLASSES).map(([node, cls]) => [node, Decoration.line({ class: cls })]),
);

/**
 * Walk the parse tree over the visible ranges and collect one line decoration per block line.
 *
 * Collected into a map before building because `RangeSetBuilder` requires ranges in document
 * order, while the tree is walked outside-in — a `FencedCode` nested in a `Blockquote` yields an
 * inner node whose lines the outer node already visited.
 */
function buildBlockDecorations(view: EditorView): DecorationSet {
  const byLineStart = new Map<number, Decoration[]>();
  const { doc } = view.state;

  for (const { from, to } of view.visibleRanges) {
    syntaxTree(view.state).iterate({
      from,
      to,
      enter: (node) => {
        const decoration = BLOCK_LINE_DECORATIONS[node.name];
        if (!decoration) return;

        const firstLine = doc.lineAt(node.from).number;
        const lastLine = doc.lineAt(Math.min(node.to, doc.length)).number;
        for (let n = firstLine; n <= lastLine; n += 1) {
          const lineStart = doc.line(n).from;
          const existing = byLineStart.get(lineStart);
          if (!existing) {
            byLineStart.set(lineStart, [decoration]);
          } else if (!existing.includes(decoration)) {
            existing.push(decoration);
          }
        }
      },
    });
  }

  const builder = new RangeSetBuilder<Decoration>();
  for (const lineStart of [...byLineStart.keys()].sort((a, b) => a - b)) {
    for (const decoration of byLineStart.get(lineStart) ?? []) {
      builder.add(lineStart, lineStart, decoration);
    }
  }
  return builder.finish();
}

/** Recomputes block line decorations when the doc, the viewport or the parse state changes. */
export const blockLineDecorations = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;

    constructor(view: EditorView) {
      this.decorations = buildBlockDecorations(view);
    }

    update(update: ViewUpdate): void {
      const reparsed = syntaxTree(update.startState) !== syntaxTree(update.state);
      if (update.docChanged || update.viewportChanged || reparsed) {
        this.decorations = buildBlockDecorations(update.view);
      }
    }
  },
  { decorations: (plugin) => plugin.decorations },
);

/** Editor chrome and every live-formatting rule. */
export const markdownLiveTheme = EditorView.theme({
  '&': {
    fontFamily: 'var(--font-sans)',
    fontSize: '0.875rem',
    color: 'var(--color-text-primary)',
    backgroundColor: 'var(--color-white)',
    borderRadius: 'var(--radius-input)',
    border: '1px solid var(--color-outlines)',
    // The host element is a flex column, so this makes the editor fill any min-height the consumer
    // sets on it rather than collapsing to its content and leaving a bordered gap below.
    flex: '1 1 auto',
  },
  '&.cm-focused': {
    outline: '2px solid var(--color-primary-700)',
    outlineOffset: '-1px',
  },
  '.cm-scroller': { fontFamily: 'inherit', lineHeight: '1.65', overflow: 'auto' },
  '.cm-content': { padding: '0.75rem 1rem', caretColor: 'var(--color-text-primary)' },
  '.cm-line': { padding: '0 2px' },
  '.cm-cursor, .cm-dropCursor': { borderLeftColor: 'var(--color-text-primary)' },
  '&.cm-focused .cm-selectionBackground, .cm-selectionBackground, ::selection': {
    backgroundColor: 'var(--color-primary-subtle)',
  },

  // ── Headings: size carries the hierarchy, weight separates it from body text ──
  '.cm-md-h1': { fontSize: '1.65em', fontWeight: '700', lineHeight: '1.3' },
  '.cm-md-h2': { fontSize: '1.35em', fontWeight: '700', lineHeight: '1.35' },
  '.cm-md-h3': { fontSize: '1.15em', fontWeight: '600', lineHeight: '1.4' },
  '.cm-md-h4': { fontSize: '1.05em', fontWeight: '600' },
  '.cm-md-h5': { fontWeight: '600' },
  '.cm-md-h6': { fontWeight: '600', color: 'var(--color-text-secondary)' },

  // ── Inline emphasis ──
  '.cm-md-strong': { fontWeight: '700' },
  '.cm-md-em': { fontStyle: 'italic' },
  '.cm-md-strike': { textDecoration: 'line-through' },

  // ── Code ──
  // The chip look is for *inline* code only. `CodeText` shares the `monospace` tag, so a fenced
  // block would otherwise get a background chip per line on top of its own block styling.
  '.cm-line:not(.cm-md-codeblock) .cm-md-code': {
    fontFamily: MONO,
    fontSize: '0.9em',
    backgroundColor: 'var(--color-subtle-bg)',
    borderRadius: 'var(--radius-input)',
    padding: '0.1em 0.3em',
  },
  '.cm-md-codeblock': {
    fontFamily: MONO,
    fontSize: '0.85em',
    backgroundColor: 'var(--color-subtle-bg)',
    borderLeft: '3px solid var(--color-outlines)',
  },

  // ── Links. Underlined so the affordance does not rely on colour alone (a11y guidelines) ──
  '.cm-md-link': { color: 'var(--color-primary-700)', textDecoration: 'underline' },
  '.cm-md-url': { color: 'var(--color-text-secondary)' },

  // ── Blockquotes ──
  '.cm-md-blockquote': {
    borderLeft: '3px solid var(--color-primary-subtle)',
    color: 'var(--color-text-secondary)',
    fontStyle: 'italic',
  },

  // ── Tables: monospace so the `|` separators align vertically. No table rendering. ──
  '.cm-md-table': { fontFamily: MONO, fontSize: '0.85em' },

  // ── Markdown syntax markers: dimmed, never hidden ──
  '.cm-md-syntax': { color: 'var(--color-text-placeholder)', fontWeight: '400' },
  // …except list bullets and numbers, which read as structure rather than noise.
  '.cm-md-list.cm-md-syntax': { color: 'var(--color-primary-700)', fontWeight: '600' },

  '.cm-md-hr': { color: 'var(--color-text-placeholder)' },
});

/** The complete live-formatting bundle, in the order CodeMirror should apply it. */
export const markdownLiveFormatting = [
  markdownLiveTheme,
  syntaxHighlighting(markdownSemanticClasses),
  blockLineDecorations,
];
